<template>
  <div class="container-fluid">
    <div class="d-sm-flex align-items-center justify-content-between mb-4">
      <h1 class="h3 mb-0 text-gray-800">Agent Management</h1>
    </div>

    <div class="row">
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-primary mb-g">
          <div>
            <small class="text-muted fs-sm">Total Agents</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-primary">
              {{ totalAgents }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-success mb-g">
          <div>
            <small class="text-muted fs-sm">Active</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-success">
              {{ statusCounts.online }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-warning mb-g">
          <div>
            <small class="text-muted fs-sm">Busy</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-warning">
              {{ statusCounts.busy }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-danger mb-g">
          <div>
            <small class="text-muted fs-sm">Offline</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-danger">
              {{ statusCounts.offline + statusCounts.disabled }}
            </h3>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 mt-3">
        <div class="card mb-4">
          <div class="card-header py-3">
            <h6 class="m-0 font-weight-bold text-primary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                style="vertical-align: sub;"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-filter w-5 h-5 text-primary"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              Search &amp; Filter
            </h6>
          </div>
          <div class="card-body">
            <form @submit.prevent>
              <div class="row">
                <div class="col-md-8">
                  <div class="input-group bg-white shadow-inset-2">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-transparent border-right-0">
                        <font-awesome-icon :icon="['fas', 'search']" />
                      </span>
                    </div>
                    <input
                      v-model.trim="searchQuery"
                      type="text"
                      class="form-control border-left-0 bg-transparent pl-0"
                      placeholder="Search by ID, username, name or email..."
                    >
                  </div>
                </div>

                <div class="col-md-4">
                  <select class="custom-select w-100" v-model="statusFilter">
                    <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="form-inline mb-3">
            <div class="form-group">
              <label class="pr-2">Results per page: </label>
              <select class="custom-select" v-model.number="perPage">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="15">15</option>
                <option :value="0">All</option>
              </select>
            </div>
          </div>
          <p class="text-primary ml-3">Showing <strong>{{ showingCount }}</strong> of <strong>{{ filteredCount }}</strong> agents</p>
        </div>

        <button class="btn btn-primary" type="button" @click="showModal('add')"><font-awesome-icon
            :icon="['fas', 'plus']" /> New Agent</button>
      </div>

      <div class="col-md-12" v-if="errorMessage">
        <div class="alert alert-danger">{{ errorMessage }}</div>
      </div>

      <div class="col-md-12 table-responsive">
        <table class="table table-bordered table-striped table-hover">
          <thead>
            <tr>
              <th scope="col">
                ID
              </th>
              <th scope="col">
                User Name
              </th>
              <th scope="col">
                Full Name
              </th>
              <th scope="col">
                Status
              </th>
              <th scope="col">
                Email
              </th>
              <th class="text-right" scope="col">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td colspan="6" class="text-center text-muted py-4">Loading agents...</td>
            </tr>
            <tr v-else-if="!pagedAgents.length">
              <td colspan="6" class="text-center text-muted py-4">No agents match the current filters.</td>
            </tr>
            <tr v-else v-for="agent in pagedAgents" :key="agent.id">
              <td> <a href="#" class="font-weight-bold" @click.prevent="openAgentDetails(agent)">{{ formatAgentIdentifier(agent.id) }}</a></td>
              <td>{{ agent.username }}</td>
              <td>{{ agent.name }}</td>
              <td><span :class="statusBadgeClass(agent.status)">{{ statusLabel(agent.status) }}</span></td>
              <td>{{ agent.email || '—' }}</td>
              <td class="text-right">
                <a href="#" :class="['btn', 'btn-icon', 'btn-sm', 'btn-action', agentActionClass(agent)]"
                  @click.prevent="openAgentStatusModal(agent)">
                  <font-awesome-icon :icon="agentActionIcon(agent)" />
                </a>
              </td>
            </tr>
          </tbody>
        </table>
        <nav aria-label="pagination" v-if="hasPagination">
          <ul class="pagination justify-content-end">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">Previous</a>
            </li>
            <li class="page-item" v-for="page in paginationPages" :key="`agents-page-${page}`" :class="{ active: currentPage === page }">
              <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalDetails ? 'block' : 'none' }" id="modalDetailsAgent" tabindex="-1"
      role="dialog" aria-labelledby="modalDetailsAgent" aria-hidden="true" data-backdrop="static"
      data-keyboard="false" v-if="showModalDetails">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header d-block" v-if="selectedAgent">
            <p class="h5 modal-title" id="exampleModalLongTitle">{{ selectedAgent.name }} - {{ formatAgentIdentifier(selectedAgent.id) }}</p>
            <small>
              Updated {{ formattedTimestamp(selectedAgent.lastUpdated) }}
              <span :class="statusBadgeClass(selectedAgent.status)">{{ statusLabel(selectedAgent.status) }}</span>
            </small>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('details')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" v-if="selectedAgent">
            <ul class="list-group list-group-flush">
              <li class="list-group-item"><label :style="{ width: '120px' }">User Name
                </label><strong>{{ selectedAgent.username }}</strong></li>
              <li class="list-group-item"><label :style="{ width: '120px' }">Full Name
                </label><strong>{{ selectedAgent.name }}</strong></li>
              <li class="list-group-item"><label :style="{ width: '120px' }">Email
                </label><strong>{{ selectedAgent.email || '—' }}</strong></li>
            </ul>

            <div class="col-12 my-3">
              <strong>Password Management</strong>
              <div v-if="detailsPasswordErrors.length" class="alert alert-danger mt-3 mb-0">
                <ul class="mb-0 pl-3">
                  <li v-for="(error, index) in detailsPasswordErrors" :key="`details-password-error-${index}`">{{ error }}</li>
                </ul>
              </div>
              <div v-else-if="detailsPasswordSuccess" class="alert alert-success mt-3 mb-0">
                {{ detailsPasswordSuccess }}
              </div>
              <div class="row mx-0 agent-password-row align-items-end">
                <div class="col-md-5 col-12 mt-3 px-0 pr-md-3">
                  <div class="mb-3 mb-md-0">
                    <label for="password">Password:</label>
                    <div class="input-group">
                      <input :type="showDetailsPassword ? 'text' : 'password'" v-model="detailsPasswordForm.password" class="form-control" id="password"
                        autocomplete="new-password" placeholder="Enter new password" required>
                      <div class="input-group-append" @click="showDetailsPassword = !showDetailsPassword">
                        <font-awesome-icon :icon="['fas', showDetailsPassword ? 'eye-slash' : 'eye']" class="input-group-text"
                          size="xs" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-5 col-12 mt-3 px-0 pr-md-3">
                  <div class="mb-3 mb-md-0">
                    <label for="password">Confirm Password</label>
                    <div class="input-group">
                      <input :type="showDetailsConfirmPassword ? 'text' : 'password'" v-model="detailsPasswordForm.confirmPassword" class="form-control" id="password-confirm"
                        autocomplete="new-password" placeholder="Confirm new password" required>
                      <div class="input-group-append" @click="showDetailsConfirmPassword = !showDetailsConfirmPassword">
                        <font-awesome-icon :icon="['fas', showDetailsConfirmPassword ? 'eye-slash' : 'eye']" class="input-group-text"
                          size="xs" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-2 col-12 mt-3 px-0 d-flex align-items-end justify-content-md-end">
                  <button type="button" class="btn btn-success agent-save-btn" :disabled="isSavingDetailsPassword" @click="saveAgentPassword">
                    <font-awesome-icon :icon="['fas', 'save']" class="mr-2" />
                    {{ isSavingDetailsPassword ? 'Saving...' : 'Save' }}
                  </button>
                </div>
              </div>
            </div>

            <ul class="list-group list-group-flush">
              <li class="list-group-item"><label :style="{ width: '120px' }">Created At:
                </label><strong>{{ formattedTimestamp(selectedAgent.lastUpdated) }}</strong></li>
              <li class="list-group-item"><label :style="{ width: '120px' }">Last Updated:
                </label><strong>{{ formattedTimestamp(selectedAgent.lastUpdated) }}</strong></li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalAdd ? 'block' : 'none' }" id="modalAddAgent" tabindex="-1" role="dialog"
      aria-labelledby="modalAddAgent" aria-hidden="true" data-backdrop="static" data-keyboard="false" v-if="showModalAdd">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">Create New Agent</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('add')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="agentFormErrors.length" class="alert alert-danger">
              <ul class="mb-0 pl-3">
                <li v-for="(error, index) in agentFormErrors" :key="`agent-error-${index}`">{{ error }}</li>
              </ul>
            </div>
            <ul class="list-group list-group-flush">
              <li class="list-group-item d-flex align-items-center"><label
                  :style="{ width: '120px' }">User Name*
                </label>
                <input v-model.trim="agentForm.username" type="text" class="form-control" required>
              </li>
              <li class="list-group-item d-flex align-items-center"><label
                  :style="{ width: '120px' }">Full Name*
                </label>
                <input v-model.trim="agentForm.fullName" type="text" class="form-control" required>
              </li>
              <li class="list-group-item d-flex align-items-center"><label
                  :style="{ width: '120px' }">Email*
                </label>
                <input v-model.trim="agentForm.email" type="email" class="form-control" required>
              </li>
              <li class="list-group-item d-flex align-items-center"><label
                  :style="{ width: '120px' }">Status
                </label>
                <select class="custom-select w-100" v-model="agentForm.status">
                  <option v-for="option in statusOptions.slice(1)" :key="`status-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </li>
            </ul>

            <div class="col-12 my-3">
              <strong>Password*</strong>
              <div class="d-block d-md-flex">
                <div class="col-md-6 col-12 mt-3 pl-3 pl-md-0">
                  <div class="mb-3">
                    <label for="password">Password:</label>
                    <div class="input-group">
                      <input :type="showPassword ? 'text' : 'password'" v-model="agentForm.password" class="form-control" id="add-password"
                        autocomplete="new-password" name="new-agent-password"
                        placeholder="Enter new password" required>
                      <div class="input-group-append" @click="showPassword = !showPassword">
                        <font-awesome-icon :icon="['fas', showPassword ? 'eye-slash' : 'eye']" class="input-group-text"
                          size="xs" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 col-12 mt-3 pr-3 pr-md-0">
                  <div class="mb-3">
                    <label for="password">Confirm Password:</label>
                    <div class="input-group">
                      <input :type="showConfirmPassword ? 'text' : 'password'" v-model="agentForm.confirmPassword" class="form-control" id="add-password-confirm"
                        autocomplete="new-password" name="new-agent-password-confirm"
                        placeholder="Confirm new password" required>
                      <div class="input-group-append" @click="showConfirmPassword = !showConfirmPassword">
                        <font-awesome-icon :icon="['fas', showConfirmPassword ? 'eye-slash' : 'eye']" class="input-group-text"
                          size="xs" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('add')">Cancel</button>
              <button type="button" class="btn btn-primary" :disabled="isSubmittingAgent" @click="submitNewAgent">{{ isSubmittingAgent ? 'Saving...' : 'Create Agent' }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalConfirm ? 'block' : 'none' }" id="modalDisableAgent" tabindex="-1"
      role="dialog" aria-labelledby="modalDisableAgent" aria-hidden="true" data-backdrop="static"
      data-keyboard="false" v-if="showModalConfirm">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">{{ pendingAgentAction === 'enable' ? 'Enable Agent' : 'Disable Agent' }}</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('confirm')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p v-if="pendingAgentAction === 'disable'">Are you sure you want to disable <strong>{{ pendingStatusAgent ? pendingStatusAgent.name : '' }}</strong>? This will set their status to
              inactive.</p>
            <p v-else>Are you sure you want to enable <strong>{{ pendingStatusAgent ? pendingStatusAgent.name : '' }}</strong>?</p>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('confirm')">Cancel</button>
              <button type="button" :class="['btn', pendingAgentAction === 'enable' ? 'btn-success' : 'btn-danger']" :disabled="isUpdatingAgentStatus" @click="confirmAgentStatusAction">{{ isUpdatingAgentStatus ? (pendingAgentAction === 'enable' ? 'Enabling...' : 'Disabling...') : (pendingAgentAction === 'enable' ? 'Enable' : 'Disable') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ApiService from '@/services/api.js';

const STATUS_META = {
  online: { label: 'Active', badge: 'badge border border-success text-success badge-pill' },
  busy: { label: 'Busy', badge: 'badge border border-warning text-warning badge-pill' },
  disabled: { label: 'Inactive', badge: 'badge border border-secondary text-secondary badge-pill' },
  offline: { label: 'Offline', badge: 'badge border border-danger text-danger badge-pill' },
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'online', label: 'Active' },
  { value: 'disabled', label: 'Inactive' },
  { value: 'busy', label: 'Busy' },
  { value: 'offline', label: 'Offline' },
];

export default {
  name: 'Agents',
  data: () => ({
    agents: [],
    isLoading: false,
    errorMessage: null,
    searchQuery: '',
    statusFilter: '',
    perPage: 5,
    currentPage: 1,
    showModalDetails: false,
    showModalAdd: false,
    showModalConfirm: false,
    selectedAgentId: null,
    pendingStatusAgent: null,
    pendingAgentAction: 'disable',
    isSubmittingAgent: false,
    isUpdatingAgentStatus: false,
    showPassword: false,
    showConfirmPassword: false,
    showDetailsPassword: false,
    showDetailsConfirmPassword: false,
    isSavingDetailsPassword: false,
    detailsPasswordSuccess: null,
    detailsPasswordForm: {
      password: '',
      confirmPassword: '',
    },
    detailsPasswordErrors: [],
    agentForm: {
      username: '',
      fullName: '',
      email: '',
      status: 'offline',
      password: '',
      confirmPassword: '',
    },
    agentFormErrors: [],
  }),
  computed: {
    ...mapGetters(['system']),
    totalAgents() {
      return this.enrichedAgents.length;
    },
    statusCounts() {
      return this.enrichedAgents.reduce(
        (acc, agent) => {
          const key = agent.status || 'offline';
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        { online: 0, busy: 0, offline: 0, disabled: 0 },
      );
    },
    statusOptions() {
      return STATUS_FILTER_OPTIONS;
    },
    systemStatusMap() {
      const map = new Map();
      if (this.system && Array.isArray(this.system.agents)) {
        this.system.agents.forEach((entry) => {
          if (!entry || entry.id == null) return;
          map.set(String(entry.id), entry);
        });
      }
      return map;
    },
    enrichedAgents() {
      return (this.agents || []).map((agent) => {
        const entry = this.systemStatusMap.get(agent.username) || this.systemStatusMap.get(String(agent.id));
        const persistedStatus = agent.status || 'offline';
        const status = persistedStatus === 'disabled' ? 'disabled' : (entry ? entry.status : persistedStatus);
        return {
          ...agent,
          status,
          lastUpdated: entry ? entry.timestamp : (agent.timestamp || null),
        };
      });
    },
    filteredAgents() {
      const query = (this.searchQuery || '').toLowerCase();
      return this.enrichedAgents.filter((agent) => {
        if (this.statusFilter && agent.status !== this.statusFilter) return false;
        if (!query) return true;
        const values = [
          this.formatAgentIdentifier(agent.id),
          agent.username,
          agent.name,
          agent.email,
          agent.extension,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());
        return values.some((value) => value.includes(query));
      }).slice().sort((a, b) => {
        const disabledDiff = Number(a.status === 'disabled') - Number(b.status === 'disabled');
        if (disabledDiff !== 0) return disabledDiff;
        return b.id - a.id;
      });
    },
    pagedAgents() {
      if (this.perPage === 0) return this.filteredAgents;
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredAgents.slice(start, start + this.perPage);
    },
    showingCount() {
      return this.pagedAgents.length;
    },
    filteredCount() {
      return this.filteredAgents.length;
    },
    totalPages() {
      if (this.perPage === 0) return 1;
      return Math.max(1, Math.ceil(this.filteredCount / this.perPage));
    },
    hasPagination() {
      return this.totalPages > 1;
    },
    paginationPages() {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    },
    selectedAgent() {
      return this.enrichedAgents.find((agent) => agent.id === this.selectedAgentId) || null;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
    showModalAdd(value) {
      if (!value) {
        this.resetAgentForm();
      }
    },
    showModalDetails(value) {
      if (!value) {
        this.resetDetailsPasswordForm();
      }
    },
    showModalConfirm(value) {
      if (!value) {
        this.pendingStatusAgent = null;
        this.pendingAgentAction = 'disable';
      }
    },
    perPage(value) {
      if (value < 0) this.perPage = 0;
      this.currentPage = 1;
    },
    filteredCount() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  created() {
    this.fetchAgents();
  },
  methods: {
    fetchAgents() {
      this.isLoading = true;
      this.errorMessage = null;
      ApiService.getAgents()
        .then((response) => {
          this.agents = response.data || [];
        })
        .catch((error) => {
          const message =
            (error && error.response && error.response.data && error.response.data.error) ||
            'Unable to load agents.';
          this.errorMessage = message;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    showModal(modal) {
      if (modal === 'details') {
        this.showModalDetails = true;
        this.showModalAdd = false;
        this.showModalConfirm = false;
      } else if (modal === 'add') {
        this.showModalAdd = true;
        this.showModalDetails = false;
        this.showModalConfirm = false;
      } else {
        this.showModalConfirm = true;
        this.showModalDetails = false;
        this.showModalAdd = false;
      }
    },
    hideModal(modal) {
      if (modal === 'details') {
        this.showModalDetails = false;
        this.selectedAgentId = null;
      } else if (modal === 'add') {
        this.showModalAdd = false;
      } else {
        this.showModalConfirm = false;
      }
    },
    openAgentDetails(agent) {
      this.selectedAgentId = agent.id;
      this.resetDetailsPasswordForm();
      this.showModal('details');
    },
    openAgentStatusModal(agent) {
      this.pendingStatusAgent = agent;
      this.pendingAgentAction = agent && agent.status === 'disabled' ? 'enable' : 'disable';
      this.showModal('confirm');
    },
    agentActionClass(agent) {
      return agent && agent.status === 'disabled' ? 'btn-outline-success' : 'btn-outline-danger';
    },
    agentActionIcon(agent) {
      return agent && agent.status === 'disabled' ? ['fas', 'check'] : ['fas', 'ban'];
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
    },
    formatAgentIdentifier(id) {
      if (id == null) return '';
      return `AGT${String(id).padStart(3, '0')}`;
    },
    statusMeta(status) {
      return STATUS_META[status] || STATUS_META.offline;
    },
    statusLabel(status) {
      return this.statusMeta(status).label;
    },
    statusBadgeClass(status) {
      return this.statusMeta(status).badge;
    },
    formattedTimestamp(timestamp) {
      if (!timestamp) return '—';
      const numeric = Number(timestamp);
      const ms = Number.isNaN(numeric) ? Date.parse(timestamp) : numeric * 1000;
      if (Number.isNaN(ms)) return '—';
      const date = new Date(ms);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = String(date.getDate()).padStart(2, '0');
      const month = months[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    },
    extractErrorMessage(error, fallbackMessage) {
      if (error && error.response && error.response.data) {
        const data = error.response.data;
        if (typeof data === 'string') return data;
        if (data.error) return data.error;
      }
      return fallbackMessage;
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
    },
    validateAgentForm() {
      const errors = [];
      const { username, fullName, email, password, confirmPassword } = this.agentForm;
      if (!username) errors.push('Username is required.');
      if (!fullName) errors.push('Full name is required.');
      if (!email) errors.push('Email is required.');
      if (email && !this.isValidEmail(email)) errors.push('Email format is invalid.');
      if (!password) errors.push('Password is required.');
      if (password && password.length < 6) errors.push('Password must be at least 6 characters long.');
      if (password !== confirmPassword) errors.push('Passwords must match.');
      this.agentFormErrors = errors;
      return errors.length === 0;
    },
    validateDetailsPasswordForm() {
      const errors = [];
      const { password, confirmPassword } = this.detailsPasswordForm;
      if (!password) errors.push('Password is required.');
      if (password && password.length < 6) errors.push('Password must be at least 6 characters long.');
      if (!confirmPassword) errors.push('Confirm password is required.');
      if (password && confirmPassword && password !== confirmPassword) errors.push('Passwords must match.');
      this.detailsPasswordErrors = errors;
      return errors.length === 0;
    },
    resetDetailsPasswordForm() {
      this.detailsPasswordForm = {
        password: '',
        confirmPassword: '',
      };
      this.detailsPasswordErrors = [];
      this.detailsPasswordSuccess = null;
      this.isSavingDetailsPassword = false;
      this.showDetailsPassword = false;
      this.showDetailsConfirmPassword = false;
    },
    resetAgentForm() {
      this.agentForm = {
        username: '',
        fullName: '',
        email: '',
        status: 'offline',
        password: '',
        confirmPassword: '',
      };
      this.agentFormErrors = [];
      this.isSubmittingAgent = false;
      this.showPassword = false;
      this.showConfirmPassword = false;
    },
    saveAgentPassword() {
      if (!this.selectedAgent) return;
      this.detailsPasswordErrors = [];
      this.detailsPasswordSuccess = null;
      if (!this.validateDetailsPasswordForm()) return;

      this.isSavingDetailsPassword = true;
      ApiService.updateAgentPassword(this.selectedAgent.username, this.detailsPasswordForm.password)
        .then(() => {
          this.detailsPasswordSuccess = 'Password updated successfully.';
          this.detailsPasswordForm.password = '';
          this.detailsPasswordForm.confirmPassword = '';
          this.showDetailsPassword = false;
          this.showDetailsConfirmPassword = false;
        })
        .catch((error) => {
          const message = this.extractErrorMessage(error, 'Unable to update password.');
          this.detailsPasswordErrors = [message];
        })
        .finally(() => {
          this.isSavingDetailsPassword = false;
        });
    },
    submitNewAgent() {
      this.agentFormErrors = [];
      if (!this.validateAgentForm()) return;
      this.isSubmittingAgent = true;
      const payload = {
        username: this.agentForm.username,
        fullName: this.agentForm.fullName,
        email: this.agentForm.email,
        status: this.agentForm.status,
        password: this.agentForm.password,
      };
      ApiService.createAgent(payload)
        .then(() => {
          this.fetchAgents();
          this.hideModal('add');
        })
        .catch((error) => {
          const message = this.extractErrorMessage(error, 'Unable to create agent.');
          this.agentFormErrors = [message];
        })
        .finally(() => {
          this.isSubmittingAgent = false;
        });
    },
    confirmAgentStatusAction() {
      if (!this.pendingStatusAgent) return;
      this.isUpdatingAgentStatus = true;
      this.errorMessage = null;
      const actionReq = this.pendingAgentAction === 'enable'
        ? ApiService.enableAgent(this.pendingStatusAgent.username)
        : ApiService.disableAgent(this.pendingStatusAgent.username);
      actionReq
        .then(() => {
          this.fetchAgents();
          this.hideModal('confirm');
        })
        .catch((error) => {
          const fallback = this.pendingAgentAction === 'enable' ? 'Unable to enable agent.' : 'Unable to disable agent.';
          this.errorMessage = this.extractErrorMessage(error, fallback);
        })
        .finally(() => {
          this.isUpdatingAgentStatus = false;
        });
    },
  },
};
</script>

<style scoped>
.mb-g {
  margin-bottom: 1rem;
}

.agent-password-row {
  margin-top: 0.25rem;
}

.agent-save-btn {
  height: calc(1.5em + 0.75rem + 2px);
  width: 100%;
  max-width: 145px;
}
</style>
